import { Box, InteracTransactionResponse, InteracWordmark } from 'vopay';
import { FunctionComponent } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TransactionCard, TransactionDetailsCard, ErrorInfoCard } from 'UI';

type InteracMoneyRequestTransactionInfoProps = {
  transaction: InteracTransactionResponse;
};

type FC = FunctionComponent<InteracMoneyRequestTransactionInfoProps>;

const InteracMoneyRequestTransactionInfo: FC = ({ transaction }) => {
  const { accountId } = useParams<{ accountId: string }>();
  const accountName = decodeURIComponent(new URLSearchParams(useLocation().search).get('accountName') || '');

  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.Details.TransactionDetails',
  });

  const transactionDetails = [
    {
      label: t('Label.transactionType', { defaultValue: 'Transaction Type' }),
      value: <InteracWordmark type="moneyRequest" />,
    },
    {
      label: t('Label.transactionTypeCode', { defaultValue: 'Transaction Type Code' }),
      value: transaction.TransactionTypeCode || '-',
    },
    {
      label: t('Label.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime || '-',
    },
    {
      label: t('Label.processingDate', { defaultValue: 'Processing Date' }),
      value: transaction.ProcessingDate || '-',
    },
    {
      label: t('Label.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified || '-',
    },
    {
      label: t('Label.amount', { defaultValue: 'Amount' }),
      value: transaction.Amount || '-',
    },
    {
      label: t('Label.currency', { defaultValue: 'Currency' }),
      value: transaction.Currency || '-',
    },
    {
      label: t('Label.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount || '-',
    },
    {
      label: t('Label.clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
    },
    {
      label: t('Label.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('Label.notes', { defaultValue: 'Notes' }),
      value: transaction.Notes || '-',
    },
  ];

  const moneyRequestDetails = [
    {
      label: t('Label.recipientName', { defaultValue: 'Recipient Name' }),
      value: transaction.RecipientName || '-',
    },
    {
      label: t('Label.email', { defaultValue: 'Email' }),
      value: transaction.Email || '-',
    },
    {
      label: t('Label.confirmationNumber', { defaultValue: 'Confirmation Number' }),
      value: transaction.ConfirmationNumber || '-',
    },
    {
      label: t('Label.messageForRecipient', { defaultValue: 'Message For Recipient' }),
      value: transaction.MessageForRecipient || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      <TransactionCard
        receiverName={accountName || '-'}
        amount={transaction.Amount}
        currency={transaction.Currency}
        transactionId={transaction.TransactionID}
        type="Interac Money Request"
        accountId={accountId}
        failureReason={transaction.FailureReason}
      />

      <TransactionDetailsCard
        title={t('Title.transactionDetails', { defaultValue: 'Transaction Details' })}
        details={transactionDetails}
      />

      <TransactionDetailsCard
        title={
          <>
            <InteracWordmark type="moneyRequest" /> {t('Title.details', { defaultValue: 'Details' })}
          </>
        }
        details={moneyRequestDetails}
      />

      <ErrorInfoCard
        currency={transaction.Currency}
        amount={transaction.Amount}
        lastModified={transaction.LastModified}
        failureReason={transaction.FailureReason}
      />
    </Box>
  );
};

export default InteracMoneyRequestTransactionInfo;
